<script setup lang="ts">
import { computed, useSlots } from "vue";

const props = withDefaults(
  defineProps<{
    variant?: "default" | "elevated" | "outlined";
    padding?: "none" | "sm" | "md" | "lg";
    interactive?: boolean;
    as?: "div" | "article" | "button";
  }>(),
  {
    variant: "default",
    padding: "md",
    interactive: false,
    as: "div",
  }
);

const slots = useSlots();

const variantClass = computed(() => {
  switch (props.variant) {
    case "elevated":
      return "border border-slate-200 card--elevated";
    case "outlined":
      return "border-2 border-slate-200";
    default:
      return "border border-slate-200";
  }
});

const paddingClass = computed(() => {
  switch (props.padding) {
    case "none":
      return "";
    case "sm":
      return "p-px-8";
    case "lg":
      return "p-px-24";
    default:
      return "p-px-16"; // md
  }
});

const isInteractive = computed(() => props.interactive || props.as === "button");

const rootClass = computed(() => [
  "bg-slate-0 rounded-8 overflow-hidden",
  variantClass.value,
  isInteractive.value ? "cursor-pointer transition-shadow duration-fast ease-standard focus-ring card--interactive" : "",
  props.as === "button" ? "w-full text-left" : "",
]);

const hasHeader = computed(() => !!(slots.header || slots.actions));
const hasFooter = computed(() => !!slots.footer);
</script>

<template>
  <component
    :is="as"
    :class="rootClass"
    :tabindex="interactive && as !== 'button' ? 0 : undefined"
    :type="as === 'button' ? 'button' : undefined">
    <!-- Header + Actions row -->
    <div
      v-if="hasHeader"
      class="gap-px-8 px-px-16 py-px-12 flex items-center border-b border-slate-100">
      <div class="min-w-0 flex-1">
        <slot name="header" />
      </div>
      <div
        v-if="$slots.actions"
        class="gap-px-8 flex shrink-0 items-center">
        <slot name="actions" />
      </div>
    </div>

    <!-- Default content -->
    <div :class="paddingClass">
      <slot />
    </div>

    <!-- Footer -->
    <div
      v-if="hasFooter"
      class="px-px-16 py-px-12 border-t border-slate-100">
      <slot name="footer" />
    </div>
  </component>
</template>

<style scoped>
@reference "../../../style.css";

.card--elevated {
  box-shadow: var(--shadow-card);
}

.card--interactive:hover {
  box-shadow: var(--shadow-dropdown);
}
</style>
